import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '@/services/api-client';
import type { GameChallenge, ApiError, Game } from '@/services/api-client';
import { isCurrentPeriod as checkIsCurrentPeriod } from '@/utils/challengeAccess';

interface UseCurrentChallengeReturn {
  currentChallenge: GameChallenge | null;
  challenges: GameChallenge[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isCurrentPeriod: (challenge: GameChallenge) => boolean;
}

/**
 * Hook to fetch challenges and determine the current one based on game frequency
 * @param gameId - The ID of the game
 * @param game - The game data (used to determine challenge frequency)
 * @returns Current challenge, all challenges, loading state, error state, and helper functions
 */
export function useCurrentChallenge(gameId: string | null, game: Game | null): UseCurrentChallengeReturn {
  const [challenges, setChallenges] = useState<GameChallenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchChallenges = useCallback(async () => {
    if (!gameId) {
      setChallenges([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const challengeData = await apiClient.getChallengesForGame(gameId);
      setChallenges(challengeData);
    } catch (err) {
      const apiError = err as ApiError;
      setError(new Error(apiError.message || 'Failed to fetch challenges'));
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  // Determine the current challenge based on game frequency
  const currentChallenge = useMemo(() => {
    if (!challenges.length || !game) return null;

    const now = new Date();

    // Sort challenges by publish date descending
    const sortedChallenges = [...challenges].sort((a, b) => 
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );

    // Find the most recent challenge that's been published
    for (const challenge of sortedChallenges) {
      const publishDate = new Date(challenge.publishDate);
      if (publishDate <= now) {
        return challenge;
      }
    }

    return null;
  }, [challenges, game]);

  // Helper function to determine if a challenge is from the current period
  // Uses the centralized logic from challengeAccess.ts
  const isCurrentPeriod = useCallback((challenge: GameChallenge): boolean => {
    if (!game) return false;
    return checkIsCurrentPeriod(challenge, game);
  }, [game]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return {
    currentChallenge,
    challenges,
    loading,
    error,
    refetch: fetchChallenges,
    isCurrentPeriod,
  };
}